/**
 * cart-drawer-discounts.js
 * --------------------------------------------------------------------------
 * Self-contained controller for the Anxsus cart drawer.
 *
 * It fixes / provides:
 *   1. Reliable quantity +/- and remove (does the AJAX itself, with a
 *      visible loading state) instead of relying on the theme's fragile
 *      bubbling handler.
 *   2. A LIVE bulk-discount progress bar (fill width, gems, labels and the
 *      "(N items)" header count are recomputed from the real cart on every
 *      change — no page reload, no waiting for a Liquid re-render).
 *   3. Auto-applies the correct discount CODE based on:
 *        - total cart quantity (Buy 2 / Buy 3 / Buy 5 tiers), and
 *        - an optional product-specific rule (specific product + min qty).
 *   4. Makes the "Add On" / "Complete Your Look" add buttons AJAX
 *      (no full page reload).
 *
 * IMPORTANT (backend): the discount CODES below must be created in
 * Shopify Admin → Discounts (see the merchant notes that ship with this
 * change). The theme only *applies* a code; Shopify decides the actual
 * money off at checkout based on the rule you configured there.
 * Shopify keeps only ONE storefront discount-code cookie at a time, so
 * this controller applies the single best-matching code.
 * --------------------------------------------------------------------------
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------- */
  /*  Config (defaults — overridable via #CartDrawerDiscountConfig JSON)     */
  /* ---------------------------------------------------------------------- */
  var DEFAULT_CONFIG = {
    // Quantity tiers. `qty` = minimum TOTAL items in cart to unlock.
    // `code` must match a discount code you created in Shopify Admin.
    tiers: [
      { qty: 2, code: 'BULK10', percent: 10, label: '10% OFF' },
      { qty: 3, code: 'BULK15', percent: 15, label: '15% OFF + Freebie' },
      { qty: 5, code: 'BULK25', percent: 25, label: '25% OFF + Freebie' }
    ],
    // Optional product-specific coupon. Leave productId 0 / empty to disable.
    // When the product is in the cart with at least `minQty` units, `code`
    // is applied (it takes priority over the quantity tiers).
    productRule: {
      productId: 0,            // numeric Shopify product id, e.g. 1234567890
      productHandle: '',       // OR the product handle, e.g. 'shuttle-bird-t-shirt'
      minQty: 1,
      code: '',                // e.g. 'SHUTTLEBIRD15'
      label: 'Special offer'
    },
    // When false the theme does NOT apply a code (use this if you set up
    // Shopify "Automatic discounts" — the bar is then purely visual and the
    // real saving still shows because Shopify lowers the cart total).
    autoApplyCodes: true,
    appliedAttribute: '_anx_discount' // cart attribute used for idempotency
  };

  function readConfig() {
    var cfg = JSON.parse(JSON.stringify(DEFAULT_CONFIG));
    var el = document.getElementById('CartDrawerDiscountConfig');
    if (el && el.textContent.trim()) {
      try {
        var override = JSON.parse(el.textContent);
        if (override.tiers && override.tiers.length) {
          cfg.tiers = override.tiers.filter(function (t) {
            return t && Number(t.qty) > 0;
          });
        }
        if (override.productRule) {
          cfg.productRule = Object.assign(cfg.productRule, override.productRule);
        }
        if (typeof override.autoApplyCodes === 'boolean') {
          cfg.autoApplyCodes = override.autoApplyCodes;
        }
      } catch (e) {
        /* keep defaults if the JSON block is malformed */
        console.warn('[cart-drawer] discount config JSON invalid, using defaults', e);
      }
    }
    cfg.tiers.sort(function (a, b) { return a.qty - b.qty; });
    return cfg;
  }

  var CONFIG = readConfig();

  /* ---------------------------------------------------------------------- */
  /*  Small helpers                                                          */
  /* ---------------------------------------------------------------------- */
  var ROUTES = window.routes || {};
  var URL_CHANGE = ROUTES.cart_change_url || '/cart/change.js';
  var URL_ADD = ROUTES.cart_add_url || '/cart/add.js';
  var URL_UPDATE = ROUTES.cart_update_url || '/cart/update.js';
  var URL_CART = (ROUTES.cart_url || '/cart');

  var SECTION = 'cart-drawer';
  var busy = false;
  var lastAppliedCode = null;

  // Set window.ANX_CART_DEBUG = true in the console to see detailed logs.
  function log() {
    if (window.ANX_CART_DEBUG) {
      console.log.apply(console, ['[cart-drawer]'].concat([].slice.call(arguments)));
    }
  }

  // True when running inside the Shopify Theme Editor preview, where the
  // cart is sandboxed and write requests (/cart/change) return 422.
  function isThemeEditor() {
    try {
      if (window.Shopify && window.Shopify.designMode) return true;
      if (document.documentElement.classList.contains('shopify-design-mode')) return true;
      if (window.top !== window.self) return true; // running in admin iframe
    } catch (e) {
      return true; // cross-origin access blocked => almost certainly the editor
    }
    return false;
  }

  function showCartError(msg) {
    var box =
      document.getElementById('CartDrawer-CartErrors') ||
      document.getElementById('cart-errors');
    if (box) box.textContent = msg || '';
  }

  function drawerRoot() {
    return document.getElementById('CartDrawer');
  }

  function drawerInner() {
    var root = drawerRoot();
    return root ? root.querySelector('.drawer__inner') : null;
  }

  function postJSON(url, data) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(data)
    }).then(function (r) { return r.json(); });
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function setBusy(state) {
    var items = document.querySelector('cart-drawer-items');
    if (!items) return;
    if (state) {
      items.classList.add('cart-drawer--updating');
      if (!items.querySelector('.cart-drawer__spinner')) {
        var s = document.createElement('div');
        s.className = 'cart-drawer__spinner';
        items.appendChild(s);
      }
    } else {
      items.classList.remove('cart-drawer--updating');
      var sp = items.querySelector('.cart-drawer__spinner');
      if (sp) sp.remove();
    }
  }

  /* ---------------------------------------------------------------------- */
  /*  Re-render helpers                                                      */
  /* ---------------------------------------------------------------------- */
  // Replace the header cart-count bubble from the `cart-icon-bubble` section.
  function applyBubbleHTML(html) {
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || !html) return;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var inner = parsed.querySelector('.shopify-section') || parsed.body;
    if (inner) bubble.innerHTML = inner.innerHTML;
  }

  /* ------------------------------------------------------------------ */
  /*  Money formatting (reads the store's format from an existing node)  */
  /* ------------------------------------------------------------------ */
  function moneyFormat() {
    if (window.Shopify && window.Shopify.money_format) return window.Shopify.money_format;
    return '{{amount}}';
  }

  function formatMoney(cents) {
    var fmt = moneyFormat();
    var amount = (cents / 100);
    function withCommas(num, decimals) {
      var n = (decimals === 0)
        ? Math.round(num).toString()
        : num.toFixed(decimals === undefined ? 2 : decimals);
      var parts = n.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }
    var out = fmt;
    out = out.replace(/\{\{\s*amount\s*\}\}/g, withCommas(amount, 2));
    out = out.replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withCommas(amount, 0));
    out = out.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCommas(amount, 2));
    out = out.replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withCommas(amount, 0));
    if (out === fmt) out = '₹' + withCommas(amount, 2); // fallback if no token matched
    return out;
  }

  /* ------------------------------------------------------------------ */
  /*  Direct DOM patch — used when a section re-render isn't available   */
  /*  (guarantees the drawer always reflects the real cart)             */
  /* ------------------------------------------------------------------ */
  function patchFromCart(cart) {
    // 1) Quantity inputs + per-line totals, matched by variant id
    var inputs = document.querySelectorAll('#CartDrawer .quantity__input');
    inputs.forEach(function (inp) {
      var vid = parseInt(inp.dataset.quantityVariantId, 10);
      var match = null;
      for (var i = 0; i < cart.items.length; i++) {
        if (cart.items[i].variant_id === vid) { match = cart.items[i]; break; }
      }
      var row = inp.closest('.cart-item, tr');
      if (!match) {
        if (row) row.remove(); // line was removed
        return;
      }
      inp.value = match.quantity;
      if (row) {
        var priceEl = row.querySelector('.cart-item__totals .price:not(.cart-item__old-price)');
        if (priceEl) priceEl.textContent = formatMoney(match.final_line_price);
      }
    });

    // 2) Subtotal
    document.querySelectorAll('.totals__total-value').forEach(function (el) {
      el.textContent = formatMoney(cart.total_price);
    });

    // 3) Empty-state toggle
    var cd = document.querySelector('cart-drawer');
    var cdi = document.querySelector('cart-drawer-items');
    if (cart.item_count === 0) {
      if (cd) cd.classList.add('is-empty');
      if (cdi) cdi.classList.add('is-empty');
    } else {
      if (cd) cd.classList.remove('is-empty');
      if (cdi) cdi.classList.remove('is-empty');
    }
  }

  // Try a clean section re-render; if it fails or comes back malformed,
  // fall back to patching the DOM directly from the cart object.
  function renderDrawer(cart) {
    return fetch(URL_CART + '?section_id=' + SECTION, { headers: { Accept: 'text/html' } })
      .then(function (r) {
        if (!r.ok) throw new Error('section ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('.drawer__inner');
        var hasItems = fresh && (fresh.querySelector('.cart-item') || (cart && cart.item_count === 0));
        if (!fresh || !hasItems) throw new Error('section markup unexpected');
        var live = drawerInner();
        if (live) live.innerHTML = fresh.innerHTML;
        // header bubble (best effort)
        return fetch(URL_CART + '?section_id=cart-icon-bubble')
          .then(function (r2) { return r2.ok ? r2.text() : ''; })
          .then(function (bub) { if (bub) applyBubbleHTML(bub); })
          .catch(function () {});
      })
      .catch(function (err) {
        log('section render unavailable, patching DOM instead:', err && err.message);
        if (cart) patchFromCart(cart);
      });
  }

  /* ---------------------------------------------------------------------- */
  /*  Live bulk-discount progress bar                                        */
  /* ---------------------------------------------------------------------- */
  function currentTier(itemCount) {
    var reached = null;
    for (var i = 0; i < CONFIG.tiers.length; i++) {
      if (itemCount >= CONFIG.tiers[i].qty) reached = CONFIG.tiers[i];
    }
    return reached; // null = no tier reached yet
  }

  function nextTier(itemCount) {
    for (var i = 0; i < CONFIG.tiers.length; i++) {
      if (itemCount < CONFIG.tiers[i].qty) return CONFIG.tiers[i];
    }
    return null; // all tiers reached
  }

  function updateProgressUI(cart) {
    var count = cart.item_count || 0;

    // Header "(N items)" count
    var countEl = document.querySelector('.drawer__heading-count');
    if (countEl) {
      countEl.textContent = '(' + count + ' ' + (count === 1 ? 'item' : 'items') + ')';
    }

    var fill = document.querySelector('.bulk-discount__fill');
    var track = document.querySelector('.bulk-discount__track');
    var progress = document.querySelector('.bulk-discount__progress');
    if (!fill || !track) return; // cart empty / bar not rendered

    var tiers = CONFIG.tiers;

    // Stops map cart quantity -> fill %. The gems sit at 33% / 66% / right
    // edge, so each tier threshold maps to an evenly spaced stop and the
    // fill is linearly interpolated between thresholds.
    var stops = [{ q: 0, p: 6 }];
    for (var t = 0; t < tiers.length; t++) {
      stops.push({ q: tiers[t].qty, p: Math.round(((t + 1) / tiers.length) * 100) });
    }

    var pct;
    if (count <= 0) {
      pct = stops[0].p;
    } else if (count >= stops[stops.length - 1].q) {
      pct = 100;
    } else {
      for (var s = 1; s < stops.length; s++) {
        if (count < stops[s].q) {
          var lo = stops[s - 1];
          var span = stops[s].q - lo.q || 1;
          pct = lo.p + ((count - lo.q) / span) * (stops[s].p - lo.p);
          break;
        }
      }
      if (pct == null) pct = 100;
    }

    // Force the critical visuals via INLINE styles so a stale/cached or
    // conflicting stylesheet can never hide the bar (music-player style).
    if (progress) {
      progress.style.position = 'relative';
      progress.style.height = '26px';
      progress.style.margin = '4px 12px 16px';
    }
    track.style.position = 'absolute';
    track.style.top = '50%';
    track.style.left = '0';
    track.style.right = '0';
    track.style.height = '8px';
    track.style.background = '#dfe3df';
    track.style.borderRadius = '999px';
    track.style.transform = 'translateY(-50%)';
    track.style.overflow = 'hidden';

    fill.style.height = '100%';
    fill.style.minWidth = '10px';
    fill.style.background = 'linear-gradient(90deg,#34c759,#1f9d3f)';
    fill.style.borderRadius = '999px';
    fill.style.transition = 'width .45s ease';
    fill.style.width = pct + '%';

    // Music-player style knob at the end of the fill
    var knob = document.getElementById('CartDrawer-ProgressKnob');
    if (!knob && progress) {
      knob = document.createElement('div');
      knob.id = 'CartDrawer-ProgressKnob';
      progress.appendChild(knob);
    }
    if (knob) {
      knob.style.position = 'absolute';
      knob.style.top = '50%';
      knob.style.width = '16px';
      knob.style.height = '16px';
      knob.style.background = '#fff';
      knob.style.border = '3px solid #1f9d3f';
      knob.style.borderRadius = '50%';
      knob.style.boxShadow = '0 1px 4px rgba(0,0,0,.25)';
      knob.style.transform = 'translate(-50%,-50%)';
      knob.style.transition = 'left .45s ease';
      knob.style.zIndex = '3';
      knob.style.left = pct + '%';
    }

    // Gems + labels (.active when that tier's qty is met) + inline fallback
    var gems = document.querySelectorAll('.bulk-discount__gem');
    var labels = document.querySelectorAll('.bulk-discount__lbl');
    for (var g = 0; g < gems.length; g++) {
      var need = tiers[g] ? tiers[g].qty : Infinity;
      var on = count >= need;
      var gem = gems[g];
      gem.classList.toggle('active', on);
      gem.style.position = 'absolute';
      gem.style.top = '50%';
      gem.style.width = '22px';
      gem.style.height = '22px';
      gem.style.borderRadius = '50%';
      gem.style.zIndex = '2';
      gem.style.transform = 'translate(-50%,-50%)';
      gem.style.left = (g === 0 ? 33 : g === 1 ? 66 : 100) + '%';
      gem.style.display = 'flex';
      gem.style.alignItems = 'center';
      gem.style.justifyContent = 'center';
      gem.style.fontSize = '12px';
      gem.style.fontWeight = '900';
      gem.style.color = '#fff';
      gem.style.background = on ? '#1f9d3f' : '#fff';
      gem.style.border = '2px solid ' + (on ? '#1f9d3f' : '#cfcfcf');
      gem.textContent = on ? '✓' : '';
      if (labels[g]) labels[g].classList.toggle('active', on);
    }
  }

  /* ---------------------------------------------------------------------- */
  /*  Discount status banner                                                 */
  /* ---------------------------------------------------------------------- */
  function ensureBanner() {
    var existing = document.getElementById('CartDrawer-DiscountStatus');
    if (existing) return existing;
    var bar = document.querySelector('.cart-drawer__bulk-discount');
    if (!bar) return null;
    var b = document.createElement('div');
    b.id = 'CartDrawer-DiscountStatus';
    b.className = 'cart-drawer__discount-status';
    b.innerHTML =
      '<span class="cart-drawer__discount-status-icon"></span>' +
      '<span class="cart-drawer__discount-status-text"></span>';
    bar.appendChild(b);
    return b;
  }

  function showBanner(state, html) {
    var b = ensureBanner();
    if (!b) return;
    b.classList.add('is-visible');
    b.classList.toggle('cart-drawer__discount-status--pending', state === 'pending');
    b.querySelector('.cart-drawer__discount-status-icon').textContent =
      state === 'applied' ? '✓' : '🎁';
    b.querySelector('.cart-drawer__discount-status-text').innerHTML = html;
  }

  function hideBanner() {
    var b = document.getElementById('CartDrawer-DiscountStatus');
    if (b) b.classList.remove('is-visible');
  }

  /* ---------------------------------------------------------------------- */
  /*  Decide + apply the best discount code                                  */
  /* ---------------------------------------------------------------------- */
  function productRuleMatch(cart) {
    var pr = CONFIG.productRule;
    if (!pr || !pr.code) return false;
    var qty = 0;
    cart.items.forEach(function (it) {
      var byId = pr.productId && it.product_id === Number(pr.productId);
      var byHandle = pr.productHandle && it.handle === pr.productHandle;
      if (byId || byHandle) qty += it.quantity;
    });
    return qty >= (pr.minQty || 1);
  }

  function chooseCode(cart) {
    // product-specific rule wins (single-cookie limit)
    if (productRuleMatch(cart)) {
      return { code: CONFIG.productRule.code, label: CONFIG.productRule.label };
    }
    var tier = currentTier(cart.item_count || 0);
    return tier ? { code: tier.code, label: tier.label } : null;
  }

  function applyDiscountCookie(code) {
    // Hitting /discount/CODE sets Shopify's storefront discount cookie which
    // carries through to checkout. We don't navigate — just fire the request.
    return fetch('/discount/' + encodeURIComponent(code), {
      method: 'GET',
      redirect: 'follow',
      credentials: 'same-origin'
    }).catch(function () { /* non-fatal */ });
  }

  function syncDiscount(cart) {
    var chosen = chooseCode(cart);

    // Messaging for the next reward to chase
    var nt = nextTier(cart.item_count || 0);

    if (!chosen) {
      if (nt) {
        var need = nt.qty - (cart.item_count || 0);
        showBanner('pending',
          'Add <strong>' + need + ' more</strong> to unlock <strong>' + nt.label + '</strong>');
      } else {
        hideBanner();
      }
      return Promise.resolve();
    }

    // Visual-only mode (merchant uses Shopify Automatic discounts):
    // never touch the discount cookie, just show the unlocked state.
    if (!CONFIG.autoApplyCodes) {
      bannerApplied(chosen, nt, cart);
      return Promise.resolve();
    }

    // Already applied this exact code? just refresh the message.
    if (lastAppliedCode === chosen.code) {
      bannerApplied(chosen, nt, cart);
      return Promise.resolve();
    }

    lastAppliedCode = chosen.code;
    return applyDiscountCookie(chosen.code)
      .then(function () {
        // persist for idempotency across reloads (attribute-only update,
        // no pubsub so we don't loop)
        return postJSON(URL_UPDATE, {
          attributes: (function () {
            var a = {};
            a[CONFIG.appliedAttribute] = chosen.code;
            return a;
          })()
        }).catch(function () {});
      })
      .then(function () { bannerApplied(chosen, nt, cart); });
  }

  function bannerApplied(chosen, nt, cart) {
    var saved = cart.total_discount || 0;
    var msg;
    if (saved > 0) {
      // a real discount is already reducing the cart total
      msg = '<strong>' + chosen.label + '</strong> applied — you saved ' +
        '<strong>' + formatMoney(saved) + '</strong>';
    } else if (CONFIG.autoApplyCodes) {
      msg = '<strong>' + chosen.label + '</strong> unlocked — code <code>' +
        chosen.code + '</code> applies at checkout';
    } else {
      msg = '<strong>' + chosen.label + '</strong> unlocked — applied automatically';
    }
    if (nt) {
      var need = nt.qty - (cart.item_count || 0);
      msg += '<br>Add <strong>' + need + ' more</strong> for <strong>' + nt.label + '</strong>';
    }
    showBanner('applied', msg);
  }

  /* ---------------------------------------------------------------------- */
  /*  After any cart mutation: refresh everything                            */
  /* ---------------------------------------------------------------------- */
  // Show the REAL money saved (works automatically for Shopify
  // "Automatic discounts", which actually lower the cart total). This is
  // what makes the saving visible on the subtotal area inside the drawer.
  function updateSavings(cart) {
    var footer = document.querySelector('.cart-drawer__footer');
    var saved = cart.total_discount || 0;
    var el = document.getElementById('CartDrawer-Savings');

    if (!saved || saved <= 0) {
      if (el) el.remove();
      return;
    }
    if (!el && footer) {
      el = document.createElement('div');
      el.id = 'CartDrawer-Savings';
      el.className = 'cart-drawer__savings';
      var totals = footer.querySelector('.totals');
      footer.insertBefore(el, totals || footer.firstChild);
    }
    if (el) {
      el.innerHTML =
        '<span class="cart-drawer__savings-tag">You’re saving</span>' +
        '<span class="cart-drawer__savings-amt">' + formatMoney(saved) + '</span>';
    }
  }

  function refreshAll(cartFromResponse) {
    var p = cartFromResponse ? Promise.resolve(cartFromResponse) : getCart();
    return p.then(function (cart) {
      updateProgressUI(cart);
      updateSavings(cart);
      return syncDiscount(cart).then(function () { return cart; });
    });
  }

  /* ---------------------------------------------------------------------- */
  /*  Core mutations                                                         */
  /* ---------------------------------------------------------------------- */
  // NOTE: we deliberately do NOT send the `sections` param on cart
  // mutations. The core /cart/change.js + /cart/add.js endpoints always
  // succeed on their own; section rendering is done separately so a theme
  // / app Liquid issue can never block the actual cart update.
  function changeLine(line, quantity) {
    if (busy) return;
    busy = true;
    setBusy(true);
    log('changeLine', { line: line, quantity: quantity });

    postJSON(URL_CHANGE, { line: line, quantity: quantity })
      .then(function (state) {
        if (state && state.status && state.status >= 400) {
          var reason = state.description || state.message || ('HTTP ' + state.status);
          console.warn('[cart-drawer] Shopify rejected the change (' +
            state.status + '): ' + reason);
          if (isThemeEditor()) {
            console.warn(
              '[cart-drawer] You are inside the Shopify THEME EDITOR. ' +
              'Cart changes are sandboxed here and return 422. ' +
              'Click "Preview" (top-right) / open the live store URL in a ' +
              'normal browser tab to test add/qty/checkout.'
            );
          }
          showCartError(
            isThemeEditor()
              ? 'Cart changes don’t work inside the theme editor — open the live store to test.'
              : reason
          );
          return getCart();
        }
        showCartError('');
        return state; // full cart object
      })
      .then(function (cart) {
        return renderDrawer(cart).then(function () { return refreshAll(cart); });
      })
      .catch(function (e) {
        console.error('[cart-drawer] change failed:', e);
        // even on a render error the cart is saved server-side — resync
        return getCart().then(function (c) { patchFromCart(c); return refreshAll(c); });
      })
      .finally(function () {
        busy = false;
        setBusy(false);
      });
  }

  function addToCartForm(form) {
    if (busy) return;
    busy = true;
    setBusy(true);

    var fd = new FormData(form);
    var body = {};
    fd.forEach(function (v, k) { body[k] = v; });
    log('addToCart', body);

    postJSON(URL_ADD, body)
      .then(function (res) {
        if (res && res.status && res.status >= 400) {
          log('add rejected by Shopify:', res.description || res.message);
        }
        return getCart();
      })
      .then(function (cart) {
        var cd = document.querySelector('cart-drawer');
        if (cd && cd.classList && !cd.classList.contains('active') && typeof cd.open === 'function') {
          cd.open();
        }
        return renderDrawer(cart).then(function () { return refreshAll(cart); });
      })
      .catch(function (e) { console.error('[cart-drawer] add failed:', e); })
      .finally(function () {
        busy = false;
        setBusy(false);
      });
  }

  /* ---------------------------------------------------------------------- */
  /*  Event delegation (survives innerHTML re-renders)                       */
  /* ---------------------------------------------------------------------- */
  function inDrawer(node) {
    return node && node.closest && node.closest('#CartDrawer');
  }

  // 1-based Shopify line index from the row's live position (stays correct
  // even if the DOM was patched rather than fully re-rendered).
  function lineIndexOf(node) {
    var row = node.closest && node.closest('.cart-item, tr[id^="CartDrawer-Item"]');
    if (!row) return NaN;
    var rows = document.querySelectorAll(
      '#CartDrawer .cart-item, #CartDrawer tr[id^="CartDrawer-Item"]'
    );
    var pos = Array.prototype.indexOf.call(rows, row);
    if (pos !== -1) return pos + 1;
    // fallback to the Liquid-rendered index
    var inp = row.querySelector('.quantity__input');
    return inp ? parseInt(inp.dataset.index, 10) : NaN;
  }

  // Quantity changed (covers +/- buttons AND manual typing). We listen in the
  // CAPTURE phase and stop propagation so the theme's own bubbling handler
  // does not also fire and race with us.
  document.addEventListener(
    'change',
    function (e) {
      var input = e.target;
      if (!input || !input.classList || !input.classList.contains('quantity__input')) return;
      if (!inDrawer(input)) return;

      e.stopImmediatePropagation();

      var line = lineIndexOf(input);
      var qty = parseInt(input.value, 10);
      if (isNaN(line)) return;
      if (isNaN(qty) || qty < 0) qty = 0;

      var min = parseInt(input.dataset.min, 10);
      if (!isNaN(min) && qty > 0 && qty < min) qty = min;
      var max = parseInt(input.getAttribute('max'), 10);
      if (!isNaN(max) && qty > max) {
        qty = max;
        input.value = max;
      }
      changeLine(line, qty);
    },
    true
  );

  // Remove button
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest && e.target.closest('.cart-remove-button, cart-remove-button');
      if (!btn || !inDrawer(btn)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      var line = lineIndexOf(btn);
      if (!isNaN(line)) changeLine(line, 0);
    },
    true
  );

  // "Add On" / "Complete Your Look" forms -> AJAX
  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      var action = form.getAttribute('action') || '';
      if (!inDrawer(form)) return;
      if (action.indexOf('/cart/add') === -1) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      addToCartForm(form);
    },
    true
  );

  // Checkout button — guarantees "Proceed to buy" ALWAYS opens checkout,
  // no matter what app/handler/markup is in the way. Capture phase so we
  // run before anything else; multiple fallbacks so it can never "do
  // nothing".
  function goToCheckout() {
    var form =
      document.getElementById('CartDrawer-Form') ||
      document.querySelector('#CartDrawer form[action*="/cart"]');
    // Hard fallback: if navigation hasn't happened shortly, force it.
    setTimeout(function () {
      try { window.location.href = '/checkout'; } catch (e) {}
    }, 700);
    try {
      if (form) {
        if (!form.querySelector('input[name="checkout"]')) {
          var hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'checkout';
          hidden.value = '';
          form.appendChild(hidden);
        }
        form.submit(); // native POST /cart -> 302 /checkout (keeps note)
        return;
      }
    } catch (err) {
      log('checkout form submit failed', err);
    }
    window.location.href = '/checkout';
  }

  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest && e.target.closest(
        '#CartDrawer-Checkout, [name="checkout"], .cart__checkout-button, .cart__checkout-button--proceed'
      );
      if (!btn || !inDrawer(btn)) return;
      if (btn.hasAttribute('disabled')) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      log('checkout clicked -> going to checkout');
      goToCheckout();
    },
    true
  );

  /* ---------------------------------------------------------------------- */
  /*  External cart updates (e.g. add-to-cart from a product page)           */
  /* ---------------------------------------------------------------------- */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === 'cart-drawer-discounts') return;
      refreshAll(event && event.cartData && event.cartData.items ? event.cartData : null);
    });
  }

  /* ---------------------------------------------------------------------- */
  /*  Init                                                                   */
  /* ---------------------------------------------------------------------- */
  function init() {
    console.log(
      '%c[cart-drawer] controller loaded. Type ANX_CART_DEBUG=true for verbose logs.',
      'color:#f26522'
    );
    if (isThemeEditor()) {
      console.warn(
        '%c[cart-drawer] THEME EDITOR detected. The cart is sandboxed here ' +
        '(add / qty / checkout will fail with 422). Test on the LIVE store: ' +
        'open the store URL in a normal browser tab, not the editor preview.',
        'color:#c0392b;font-weight:bold'
      );
    }
    getCart()
      .then(function (cart) {
        log('init cart', cart);
        if (cart.attributes && cart.attributes[CONFIG.appliedAttribute]) {
          lastAppliedCode = cart.attributes[CONFIG.appliedAttribute];
        }
        updateProgressUI(cart);
        updateSavings(cart);
        return syncDiscount(cart);
      })
      .catch(function (e) { console.error('[cart-drawer] init failed:', e); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
